import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearCompanyFeatures, setCompanyFeatures } from 'src/app/reducers/company-features.reducer';
import type { RootState } from 'src/app/store';
import type { CompanyData } from 'src/common/interfaces/companies';
import { type CompanyFeatureName, isFeatureEnabled } from 'src/common/utils/company-features';
import { useFeatureIsOn } from '../growthbook';

/**
 * Hook to manage company features in the Redux store.
 * Provides methods to set/clear features and check if features are enabled.
 */
export const useCompanyFeatures = () => {
  const dispatch = useDispatch();
  const features = useSelector((state: RootState) => state.companyFeatures.features);
  const isTeamsPlanEnabled = useFeatureIsOn('business-teams-checkout');

  const setFeatures = useCallback(
    (features: CompanyData['features'] | null) => {
      dispatch(setCompanyFeatures(features));
    },
    [dispatch],
  );

  const clearFeatures = useCallback(() => {
    dispatch(clearCompanyFeatures());
  }, [dispatch]);

  const checkFeatureEnabled = useCallback(
    (featureName: CompanyFeatureName): boolean => {
      if (!isTeamsPlanEnabled) return true;

      return isFeatureEnabled(features, featureName);
    },
    [features, isTeamsPlanEnabled],
  );

  return {
    features,
    setFeatures,
    clearFeatures,
    checkFeatureEnabled,
  };
};
